/**
 * MicButton — Tap to dictate. Uses the free, on-device Web Speech API.
 * Hides itself entirely when the browser doesn't support speech recognition.
 */
import { useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface MicButtonProps {
  onTranscript: (text: string) => void;
  ariaLabel?: string;
  className?: string;
  size?: 'icon' | 'sm';
}

export function MicButton({
  onTranscript,
  ariaLabel = 'Dictate',
  className,
  size = 'icon',
}: MicButtonProps) {
  const { toast } = useToast();
  const { isSupported, isListening, error, start, stop } = useSpeechToText({
    onFinal: (text) => {
      if (text) onTranscript(text);
    },
  });

  useEffect(() => {
    if (error) {
      toast({ title: 'Voice input', description: error, variant: 'destructive' });
    }
  }, [error, toast]);

  if (!isSupported) return null;

  const handleClick = () => {
    if (isListening) stop();
    else start();
  };

  return (
    <Button
      type="button"
      size={size}
      variant={isListening ? 'default' : 'secondary'}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={isListening}
      className={cn(
        'shrink-0 transition-all motion-safe:duration-200',
        isListening &&
          'bg-red-500 hover:bg-red-600 text-white ring-2 ring-red-300 motion-safe:animate-pulse',
        className,
      )}
    >
      {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </Button>
  );
}
